/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, cleanCanvasSnapshot } from './store/useStore';
import { useAuthStore } from './store/useAuthStore';
import { canvasService } from './services/canvas.service';
import {
  hasVisitorDataToMigrate,
  migrateVisitorData,
  purgeVisitorKeys,
} from './services/visitor.migration.service';
import { Loader2 } from 'lucide-react';
import { ToastContainer } from './components/ToastContainer';
import { AppRoutes } from './router';
import { initELK } from './lib/elk-instance';

export default function App() {
  const { t } = useTranslation();
  const fetchTasks = useStore((s) => s.fetchTasks);
  const fetchDomains = useStore((s) => s.fetchDomains);
  const fetchGoals = useStore((s) => s.fetchGoals);
  const fetchEvents = useStore((s) => s.fetchEvents);
  const fetchNotes = useStore((s) => s.fetchNotes);
  const fetchContacts = useStore((s) => s.fetchContacts);
  const fetchHabits = useStore((s) => s.fetchHabits);
  const fetchInboxItems = useStore((s) => s.fetchInboxItems);

  const { session, isLoading, initialize, isPasswordRecovery } = useAuthStore();

  // VB-024B.3 — migration banner state + anti-double-trigger guard
  const [migrationBanner, setMigrationBanner] = useState<string | null>(null);
  const migrationAttemptedRef = useRef(false);
  // Records the actual auth mode used at submit time (login vs signup).
  // Set by Auth component via onAuthSuccess before onAuthStateChange fires.
  const lastAuthModeRef = useRef<'login' | 'signup' | null>(null);
  // ── Init ELK layout engine ────────────────────────────────────────────────────
  useEffect(() => {
    initELK().catch((err) => console.error('[App] ELK init failed:', err));
  }, []);
  // ── Init auth ────────────────────────────────────────────────────────────
  useEffect(() => {
    initialize();
  }, [initialize]);

  // ── Fetch entities on login ──────────────────────────────────────────────
  useEffect(() => {
    // Bug 2: skip all fetches while in password-recovery mode — the session exists
    // but the user hasn't authenticated yet, and early fetch failures cascade into toasts.
    if (session && !isPasswordRecovery) {
      fetchDomains().then(() => {
        fetchGoals();
        fetchEvents();
        fetchContacts();
        fetchTasks();
        fetchNotes();
        fetchHabits();
        fetchInboxItems();
      });
    }
  }, [
    fetchDomains,
    fetchGoals,
    fetchEvents,
    fetchContacts,
    fetchTasks,
    fetchNotes,
    fetchHabits,
    fetchInboxItems,
    isPasswordRecovery,
    session,
  ]);

  // VB-021/VB-023 — Load canvas from Supabase on login.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId || isPasswordRecovery) return;
    useStore.getState().loadCanvasFromRemote(userId);
  }, [session?.user?.id, isPasswordRecovery]);

  // VB-024B.3 — Visitor → account migration
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;
    if (migrationAttemptedRef.current) return;
    migrationAttemptedRef.current = true;

    const mode = lastAuthModeRef.current;

    if (mode !== 'signup') {
      purgeVisitorKeys();
      return;
    }

    if (!hasVisitorDataToMigrate()) return;

    migrateVisitorData(userId)
      .then((result) => {
        const total = result.migratedGoals + result.migratedTasks + result.migratedNotes;
        if (total > 0) {
          useStore.getState().fetchGoals();
          useStore.getState().fetchTasks();
          useStore.getState().fetchNotes();
          useStore.getState().loadCanvasFromRemote(userId);
        }
        if (result.success) {
          setMigrationBanner(t('app.visitor_import_success'));
        } else {
          purgeVisitorKeys();
          setMigrationBanner(t('app.visitor_import_failed'));
        }
        setTimeout(() => setMigrationBanner(null), 8000);
      })
      .catch((err) => {
        console.error('[App] visitor migration unexpected error:', err);
        purgeVisitorKeys();
        setMigrationBanner(t('app.visitor_import_failed'));
        setTimeout(() => setMigrationBanner(null), 8000);
      });
  }, [session?.user?.id]);

  // VB-021 — Auto-save canvas to Supabase (debounced 2 s).
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;
    let debounceTimer: ReturnType<typeof setTimeout>;
    let lastSavedKey: string | null = null;
    const unsubscribe = useStore.subscribe((state, prev) => {
      if (state.isHydratingCanvas || !state.hasLoadedRemoteCanvas) return;
      if (state.nodes === prev.nodes && state.edges === prev.edges) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const activeBrainId = useStore.getState().activeBrainId;
        if (!activeBrainId) return;
        const clean = cleanCanvasSnapshot(state.nodes, state.edges);
        const key = JSON.stringify({
          n: clean.nodes.map((n) => `${n.id}:${n.position.x}:${n.position.y}`),
          e: clean.edges.map((e) => `${e.id}:${JSON.stringify((e.data as any)?.waypoints ?? [])}`),
        });
        if (key === lastSavedKey) return;
        lastSavedKey = key;
        canvasService.saveCanvas(userId, activeBrainId, clean.nodes, clean.edges);
      }, 2000);
    });
    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, [session?.user?.id]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-zinc-950 flex items-center justify-center text-zinc-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <AppRoutes />
      {/* VB-024B.3 — Migration success banner: auto-dismisses after 8 s */}
      {migrationBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-zinc-800 border border-zinc-700/80 text-zinc-200 text-xs shadow-xl">
          <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
          {migrationBanner}
        </div>
      )}
      <ToastContainer />
    </>
  );
}
